import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { css } from "styled-system/css";
import { Flex } from "styled-system/jsx";

function getScrollParent(node: HTMLElement): HTMLElement | null {
  let parent = node.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(`${overflow}${overflowY}`)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

type StickyHeaderProps = PropsWithChildren<{
  /** Offset from the scrollport. Nested headers sit below the section bar. */
  top?: string | number;
}>;

export const StickyHeader = ({ children, top = 0 }: StickyHeaderProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollParent = getScrollParent(sentinel);
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { root: scrollParent, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden style={{ height: 1, marginTop: -1 }} />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        alignSelf="flex-start"
        w="full"
        position="sticky"
        top={top}
        zIndex={10}
        bg="bg.wa.light"
        borderBottom="2px solid"
        borderBottomColor="border.dark"
        className={css({
          pt: isStuck ? "100" : "0",
          pb: "100",
          px: isStuck ? "200" : "0",
          boxShadow: isStuck ? "0 4px 12px rgba(0, 0, 0, 0.12)" : "none",
          transition: "box-shadow 0.2s ease, padding 0.2s ease",
        })}
      >
        {children}
      </Flex>
    </>
  );
};
